import { useSyncExternalStore } from 'react'

import type { PlatformUserInfo } from '@/bindings/PlatformUserInfo'
import type { RestaurantUserInfo } from '@/bindings/RestaurantUserInfo'

/** Cookies are per-system (`romi_session` / `romi_platform_session`), so auth state is too. */
export type System = 'restaurant' | 'platform'

export type UserOf<S extends System> = S extends 'restaurant' ? RestaurantUserInfo : PlatformUserInfo

export type AuthState<S extends System = System> =
  | { status: 'unknown' }
  | { status: 'anonymous' }
  | {
      status: 'authenticated'
      user: UserOf<S>
      /** The only auth datum the client holds; the session cookie is HttpOnly. */
      csrfToken: string
    }

const UNKNOWN: AuthState = { status: 'unknown' }
const ANONYMOUS: AuthState = { status: 'anonymous' }

const states: Record<System, AuthState> = { restaurant: UNKNOWN, platform: UNKNOWN }
const listeners = new Set<() => void>()

function set(system: System, next: AuthState) {
  states[system] = next
  for (const l of listeners) l()
}

export function getAuth<S extends System>(system: S): AuthState<S> {
  return states[system] as AuthState<S>
}

export function setAuthenticated<S extends System>(
  system: S,
  session: { user: UserOf<S>; csrfToken: string },
) {
  set(system, {
    status: 'authenticated',
    user: session.user,
    csrfToken: session.csrfToken,
  })
}

/** Patch the live session (profile edits); no-op when not authenticated. */
export function updateAuth<S extends System>(system: S, patch: { user?: UserOf<S> }) {
  const current = states[system]
  if (current.status !== 'authenticated') return
  set(system, { ...current, ...patch })
}

/** 401 is a normal state (T15): forget the session, never throw. */
export function clearAuth(system: System) {
  if (states[system] !== ANONYMOUS) set(system, ANONYMOUS)
}

/** Test-only: back to "never asked". */
export function resetAuthForTests() {
  states.restaurant = UNKNOWN
  states.platform = UNKNOWN
  for (const l of listeners) l()
}

export function subscribeAuth(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useAuth<S extends System>(system: S): AuthState<S> {
  return useSyncExternalStore(subscribeAuth, () => getAuth(system))
}
